import {
  Home,
  Building,
  Sofa,
  GraduationCap,
  MapPin,
  Lightbulb,
  Truck,
} from "lucide-react";

/**
 * Branded blog tile used in place of bespoke per-post photography.
 * Composes a real GoVans van photograph (van1 / van2) with a dark navy gradient
 * overlay + category-specific lucide icon + category label. Each category gets a
 * unique icon + photo combination so the 6 blog posts feel visually distinct
 * even though they share a small asset library.
 */

const CATEGORY_CONFIG = {
  "Moving Tips":       { icon: Lightbulb,      image: "/img/van2.webp", accent: "#0055FF" },
  "House Moves":       { icon: Home,           image: "/img/van1.webp", accent: "#0055FF" },
  "Furniture Delivery":{ icon: Sofa,           image: "/img/van2.webp", accent: "#FF6B00" },
  "Kildare":           { icon: MapPin,         image: "/img/van1.webp", accent: "#FF6B00" },
  "Office Moves":      { icon: Building,       image: "/img/van2.webp", accent: "#0055FF" },
  "Student Moves":     { icon: GraduationCap,  image: "/img/van1.webp", accent: "#FF6B00" },
};

const FALLBACK = { icon: Truck, image: "/img/van2.webp", accent: "#0055FF" };

export default function BlogTile({ category, size = "card" }) {
  const cfg = CATEGORY_CONFIG[category] || FALLBACK;
  const Icon = cfg.icon;

  const iconBoxSize = size === "hero" ? "w-16 h-16 sm:w-20 sm:h-20" : "w-12 h-12 sm:w-14 sm:h-14";
  const iconSize = size === "hero" ? "w-8 h-8 sm:w-10 sm:h-10" : "w-6 h-6 sm:w-7 sm:h-7";
  const categorySize = size === "hero" ? "text-xs sm:text-sm" : "text-[10px] sm:text-xs";
  const wordmarkSize = size === "hero" ? "text-xl sm:text-2xl" : "text-sm sm:text-base";

  return (
    <div className="relative w-full h-full overflow-hidden bg-[#0B1E36]">
      {/* Real GoVans branded van photo */}
      <img
        src={cfg.image}
        alt=""
        width="960"
        height="600"
        loading="lazy"
        decoding="async"
        aria-hidden="true"
        className="absolute inset-0 w-full h-full object-cover"
      />

      {/* Dark navy gradient overlay — keeps imagery branded + ensures legible text */}
      <div
        aria-hidden="true"
        className="absolute inset-0"
        style={{
          background:
            "linear-gradient(135deg, rgba(11,30,54,0.92) 0%, rgba(11,30,54,0.72) 55%, rgba(0,85,255,0.55) 100%)",
        }}
      />

      {/* Top-left accent bar */}
      <div className="absolute top-0 left-0 h-1 w-16" style={{ backgroundColor: cfg.accent }} />

      {/* Category icon (top-right) */}
      <div
        aria-hidden="true"
        className={`absolute top-4 right-4 sm:top-6 sm:right-6 ${iconBoxSize} flex items-center justify-center border-2 backdrop-blur-sm`}
        style={{ borderColor: `${cfg.accent}80`, backgroundColor: "rgba(255,255,255,0.04)" }}
      >
        <Icon
          className={`${iconSize} text-white`}
          strokeWidth={1.5}
          aria-hidden="true"
        />
      </div>

      {/* Bottom-left: category label + wordmark */}
      <div className="absolute bottom-4 left-4 sm:bottom-6 sm:left-6 flex flex-col gap-2 z-10">
        <span
          className={`${categorySize} font-bold tracking-[0.25em] uppercase`}
          style={{ color: cfg.accent }}
        >
          {category}
        </span>
        <span className={`${wordmarkSize} font-extrabold tracking-tight text-white leading-none`}>
          GoVans<span style={{ color: cfg.accent }}>.ie</span>
        </span>
        <span className="text-[10px] sm:text-xs text-white/50 tracking-wide uppercase">
          Dublin Moving Guide
        </span>
      </div>
    </div>
  );
}
